import _ from "lodash";
import PropTypes from "prop-types";
import React, { memo, useContext } from "react";
import { createUseStyles } from "react-jss";
import { Tooltip, Whisper } from "rsuite";
import {
  countries,
  StyleTypeContext,
  TranslationContext,
} from "../../../umbilical_tools";
import { InlineIcon } from "@iconify/react";
import { useHistory } from "react-router-dom";
import jssVariables from "../../../jss_setup";
import RatingPanel from "../../../components/common/RatingPanel";

export function onViewActivityDetails({
  history,
  activity_uid,
  page_session_id,
}) {
  history.push({
    pathname: `/activity_details/${page_session_id}`,
    search: `?activity_uid=${activity_uid}`,
  });
}

const activityOverlayStyles = createUseStyles({
  activityOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    opacity: 100,
    zIndex: 1,
    cursor: "pointer",
  },
  [`@media ${jssVariables.media.smallscreen}`]: {
    activityOverlay: {
      display: "none",
    },
  },
});
export var ActivityOverlay = ({
  activity_uid,
  search_uid,
  page_session_id,
}) => {
  const classes = activityOverlayStyles();
  const history = useHistory();
  return (
    <div
      className={classes.activityOverlay}
      onClick={() =>
        onViewActivityDetails({
          history,
          activity_uid,
          search_uid,
          page_session_id,
        })
      }
    />
  );
};
ActivityOverlay.propTypes = {
  activity_uid: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  search_uid: PropTypes.string,
  session_id: PropTypes.string,
};
ActivityOverlay = memo(ActivityOverlay);

const locationStyles = createUseStyles({
  location: (props) => ({
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: "17rem",
    alignItems: "baseline",
    ..._.get(props, "customStyles.location"),
  }),
  locationText: (props) => ({
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    ..._.get(props, "customStyles.locationText"),
  }),
  locationMapIconContainer: (props) => ({
    display: "none",
    ..._.get(props, "customStyles.locationMapIconContainer"),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    location: (props) => ({
      display: "grid",
      gridGap: `calc(${jssVariables.normal_gap} / 2)`,
      gridTemplateColumns: "max-content max-content",
      fontSize: "medium",
      maxWidth: "40rem",
      ..._.get(props, "customStyles.location.smallscreen"),
    }),
    locationMapIconContainer: (props) => ({
      display: "grid",
      cursor: "pointer",
      ..._.get(props, "customStyles.locationMapIconContainer.smallscreen"),
    }),
    locationText: (props) => ({
      maxWidth: "40rem",
      ..._.get(props, "customStyles.locationText.smallscreen"),
    }),
  },
});
export var Location = ({
  location,
  showIcon,
  customStyles,
  showActivityOnMap,
}) => {
  const classes = locationStyles({ customStyles });

  return (
    <React.Fragment>
      <div className={classes.location}>
        {showActivityOnMap !== null && showIcon && (
          <span className={classes.locationMapIconContainer}>
            <InlineIcon icon="fa:map-marker" onClick={showActivityOnMap} />
          </span>
        )}
        <span className={classes.locationText}>{`${location.city}, ${
          countries[location.country]
        }`}</span>
      </div>
    </React.Fragment>
  );
};
Location.defaultProps = {
  location: {},
  customStyles: {},
  showIcon: true,
};
Location.propTypes = {
  location: PropTypes.object.isRequired,
  showIcon: PropTypes.bool,
  customStyles: PropTypes.object.isRequired,
  showActivityOnMap: PropTypes.func.isRequired,
};
Location = memo(Location);

export const ratingStyles = createUseStyles({
  rating: (props) => ({
    position: "relative",
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    padding: `calc(${jssVariables.normal_gap} / 2) 0`,
    alignItems: "center",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.rating", {}),
  }),
  stars: (props) => ({ ..._.get(props, "customStyles.stars", {}) }),
  totalRatings: (props) => ({
    fontSize: "large",
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    alignItems: "center",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.totalRatings", {}),
  }),
  icon: (props) => ({
    fontSize: "large",
    ..._.get(props, "customStyles.icon", {}),
  }),
});
export var Rating = ({ rating }) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { t } = useContext(TranslationContext);

  const customStyles = cStyler({
    type,
    brand,
    objName: "actRating",
  });

  const classes = ratingStyles({
    customStyles,
  });
  const speaker = <Tooltip>{t("numberOfRatings")}</Tooltip>;
  return (
    <div className={classes.rating}>
      <RatingPanel
        rating={_.get(rating, "average_rating", 0)}
        customStyles={customStyles}
      />
      <Whisper placement="top" trigger="hover" speaker={speaker}>
        <span className={classes.totalRatings}>
          ({_.get(rating, "total_ratings", 0)})
          <InlineIcon className={classes.icon} icon="dashicons:info" />
        </span>
      </Whisper>
    </div>
  );
};
Rating.defaultProps = {
  rating: {},
  customStyles: {},
};
Rating.propTypes = {
  rating: PropTypes.object.isRequired,
  customStyles: PropTypes.object.isRequired,
};
Rating = memo(Rating);
